import type { UserOrganization } from "@server/types/responseTypes";
import { create } from "zustand";

interface OrganizationState {
  // --- data ---
  externalOrganizationId?: string;
  organizations: UserOrganization[];
  organization?: UserOrganization;

  // --- status flags ---
  isLoading: boolean;
  error?: unknown;
  isSwitching: boolean;

  // --- actions ---
  /**
   * Queues an optimistic local selection and the matching external-context switch.
   *
   * The switching flag remains set until WorkOS publishes the requested
   * organization id; starting an auth redirect is not switch completion.
   *
   * @returns A promise that resolves after the provider confirms the target
   * organization and rejects with the original provider error after restoring
   * the previous selection.
   */
  setOrganization: (org: UserOrganization) => Promise<void>;
  setActiveExternalOrganization: (
    externalOrganizationId: string,
  ) => Promise<void>;
  setOrganizationState: (
    partial: Partial<
      Omit<
        OrganizationState,
        "setOrganizationState" | "setOrganization" | "addOrganization"
      >
    >,
  ) => void;
}

/**
 * Holds organization selection and external-context switching. Selection is
 * applied optimistically when its FIFO turn starts, while the external id stays
 * provider-confirmed. Failed switches roll back the selection. Each caller
 * receives its own completion or unchanged rejection, and the switching state
 * remains set until WorkOS confirms the latest requested organization.
 */
export const useOrganizationStore = create<OrganizationState>((set, get) => {
  let switchTail: Promise<void> | undefined;
  let activeSwitch:
    | {
        confirm: () => void;
        targetExternalOrganizationId: string;
        targetOrganization: UserOrganization;
      }
    | undefined;

  return {
    // initial state
    externalOrganizationId: undefined,
    organizations: [],
    organization: undefined,
    isLoading: false,
    error: undefined,
    isSwitching: false,

    // Without an external auth provider, the local application owns the active
    // organization context and can confirm it synchronously.
    setActiveExternalOrganization: async (externalOrganizationId) => {
      set({ externalOrganizationId });
    },

    // WorkOS exposes one active organization per session, so provider switches
    // must preserve request and confirmation order when callers overlap.
    setOrganization: (org) => {
      set({ isSwitching: true });
      const predecessor = switchTail ?? Promise.resolve();
      const execution = predecessor.then(async () => {
        const previousOrganization = get().organization;
        let confirmSwitch!: () => void;
        const providerConfirmation = new Promise<void>((resolve) => {
          confirmSwitch = resolve;
        });
        const currentSwitch = {
          confirm: confirmSwitch,
          targetExternalOrganizationId: org.external_id,
          targetOrganization: org,
        };

        activeSwitch = currentSwitch;
        set({ organization: org });
        try {
          await get().setActiveExternalOrganization(org.external_id);
          if (get().externalOrganizationId === org.external_id) {
            confirmSwitch();
          }
          await providerConfirmation;
        } catch (error) {
          // The external id is provider-owned and may have advanced while this
          // request failed; only the optimistic local selection is reversible.
          set({ organization: previousOrganization });
          throw error;
        } finally {
          if (activeSwitch === currentSwitch) {
            activeSwitch = undefined;
          }
        }
      });
      const settledTail = execution.then(
        () => undefined,
        () => undefined,
      );
      switchTail = settledTail;

      return execution.finally(() => {
        if (switchTail === settledTail) {
          switchTail = undefined;
          set({ isSwitching: false });
        }
      });
    },

    setOrganizationState: (partial) => {
      const confirmedExternalOrganizationId = partial.externalOrganizationId;
      const currentSwitch = activeSwitch;
      const confirmsActiveSwitch =
        typeof confirmedExternalOrganizationId === "string" &&
        confirmedExternalOrganizationId ===
          currentSwitch?.targetExternalOrganizationId;

      // Pair the provider-confirmed context with its queued selection so a
      // delayed organization-list effect cannot leave the two out of sync.
      set(
        confirmsActiveSwitch
          ? { ...partial, organization: currentSwitch.targetOrganization }
          : partial,
      );
      if (confirmsActiveSwitch) {
        currentSwitch.confirm();
      }
    },
  };
});
